import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import { API, Auth, Hub } from "aws-amplify";
import {
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { BehaviorSubject, filter } from "rxjs";
import { Button, Input, List } from "semantic-ui-react";
import { updateUserInfo } from "../graphql/mutations";
import { Providing, Render, runCatching, useBehaviorSubject, useChild } from "../hooks";
import { getUserInfo } from "../graphql/queries";
import { GraphQLQuery } from "@aws-amplify/api";
import { GetUserInfoQuery, UserInfo } from "../API";

const userKey = "optispend_user_v2"
export type User = {
  email: string;
  userId: string;
  providerName: string;
  providerType: string;
  primary: boolean;
  dateCreated: number;
  userName: string;
}
export const UserOrUndefined = createContext<BehaviorSubject<User | undefined>>(undefined as any)
export const User = createContext<BehaviorSubject<User>>(undefined as any)

export const ProvideUser: FC<PropsWithChildren> = ({children}) => {
  const userSubj = useMemo(() => new BehaviorSubject<User | undefined>(
    runCatching<User>(() => JSON.parse(localStorage.getItem(userKey) || 'jj-dev-test'))?.value
  ), [])
  useEffect(() => {
    const subscription = userSubj.subscribe(user => {
      if (user) {
        localStorage.setItem(userKey, JSON.stringify(user))
      } else {
        localStorage.removeItem(userKey)
      }
    })
    return () => subscription.unsubscribe()
  }, [userSubj])
  return <Providing _={_ => _
  (UserOrUndefined, userSubj)
  }>
    {children}
  </Providing>
}

export const Logout: FC = () => {
  const [user, setUser] = useBehaviorSubject(useContext(UserOrUndefined))
  return (<Button onClick={() => {
    setUser(undefined)
    Auth.signOut()
  }}>Log out</Button>)
}

const googleAuthToUser = (auth: any, ddbUserInfo?: UserInfo) => {
  const userData = JSON.parse(auth.attributes.identities)[0]
  return {...userData, email: auth.attributes.email, userName: ddbUserInfo?.userName || auth.attributes.email}
}

export const UserName: FC = () => {
  const [edit, setEdit] = useState(-1);
  const [inputV, setInputV] = useState('');
  const [user, setUser] = useBehaviorSubject(useContext(UserOrUndefined));
  const [userName] = useChild(useContext(User), 'userName')
  const [email] = useChild(useContext(User), 'email')

  const updateUserName = async (userName: string) => {
    try {
      setEdit(0);
      await API.graphql({
        query: updateUserInfo,
        variables: { input: {
          userId: email,
          userName: inputV
        }}
      })
      setUser({...user as any, userName})
      setEdit(-1);
    } catch (error) {
      setEdit(99999);
    }
  }

  if (!user) {
    return <div>User Not Log In</div>
  }

  const loading = edit === 0;

  if (!userName || edit >= 0) {
    return <div><List horizontal>
      <List.Item>
      <Input placeholder="User Name:" 
        loading={loading} disabled={loading}
        value={inputV}
        onChange={(e,data) => setInputV(data.value)}/>
      </List.Item>
      <List.Item>
      <Button.Group>
        <Button onClick={() => updateUserName(inputV)}>Submit</Button>
        <Button icon="cancel" onClick={() => setEdit(-1)}/>
      </Button.Group>
      </List.Item>
      </List>
      
      { edit > 10 && <div>Failed to set username, please retry</div>}
      </div>
  }

  return <List horizontal>
    <List.Item>{userName}</List.Item>
    <List.Item><Button icon='edit' size="mini" onClick={() => setEdit(1)}/></List.Item>
    </List>
}

export const Login: FC = () => {
  const [user, setUser] = useBehaviorSubject(useContext(UserOrUndefined))
  useEffect(() => {
    Hub.listen("auth", async ({ payload: { event, data }}) => {
      switch (event) {
        case "signIn":
          await getUser()
          break;
        case "signOut":
          console.log('sign out')
          setUser(undefined);
          break;
        case "customOAuthState":
          console.debug('auth custom state:', data)
      }
    });
  }, []);
  const getUser = async (): Promise<void> => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      const ddbUserInfo = await API.graphql<GraphQLQuery<GetUserInfoQuery>>({
        query: getUserInfo,
        variables: { 
          userId: currentUser.attributes.email
        }
      })
      const userData = googleAuthToUser(currentUser, ddbUserInfo.data?.getUserInfo as UserInfo)
      setUser(userData);
    } catch(error) {
      console.error("Failed to sign in.", error);
      setUser(undefined);
      Auth.signOut();
    }
  };
  return <div>
    <Button onClick={() => Auth.federatedSignIn({provider:CognitoHostedUIIdentityProvider.Google})}>login</Button>
  </div>
}

export const RequireUser: FC<PropsWithChildren<{ login?: ReactNode }>> = ({children, login = <Login/>}) => {
  const userOrUndefinedSubj = useContext(UserOrUndefined)
  const [user, setUser] = useBehaviorSubject(userOrUndefinedSubj)  
  const validateUserSession = async () => {
    try {
      await Auth.currentAuthenticatedUser();
    } catch (erro: any) {
      console.log('Current user session expired');
      setUser(undefined);
      Auth.signOut();
    }
  }
  if (user) {
    validateUserSession();
    return <Render>{function WithUser() {
      const userSubj = useMemo(() => new BehaviorSubject(user), [user])
      useEffect(() => {
        const subscription = userOrUndefinedSubj.pipe(filter(Boolean)).subscribe(userSubj)
        return () => subscription.unsubscribe()
      }, [userOrUndefinedSubj, userSubj])
      return <Providing _={_ => _
      (User, userSubj)
      }>{children}</Providing>
    }}</Render>
  } else {
    return <>{login}</>
  }
}